import { RefObject, useLayoutEffect, useRef, useState } from 'react';

/**
 * Shared card overflow measures. The interactive listing/buyer cards are a
 * listing's last level, so nothing may be hidden with no way back — these decide
 * when to show a dot separator (stats fit one line) and a Show more / "+N"
 * expander (content overflows). Both re-measure on resize and the Silka font
 * swap (text width shifts once the real cut loads).
 */

/**
 * True when the first and last elements matching `selector` inside the ref share
 * a line (same offsetTop) — i.e. the stats didn't wrap, so the dot belongs.
 * `content` is a primitive that changes when the measured text changes, so the
 * dep array stays static (no exhaustive-deps churn).
 */
export function useSameLine<T extends HTMLElement>(
  selector: string,
  content: unknown,
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [sameLine, setSameLine] = useState(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const items = el.querySelectorAll<HTMLElement>(selector);
      setSameLine(
        items.length < 2 ||
          items[0].offsetTop === items[items.length - 1].offsetTop,
      );
    };
    measure();
    // The window never resizes when a card gets its real width late — inside a
    // horizontal row, the mobile sheet, or once an image lays out — so observe
    // the element itself. Without this the only measurement is the one taken at
    // mount, when the box can still be the wrong size.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [selector, content]);
  return [ref, sameLine];
}

/**
 * True when the clamped ref element overflows its box (full height > clamped
 * height) — gates a Show more toggle. Skips measuring while `expanded` (the clamp
 * is off, so it never "overflows") and keeps the prior verdict.
 */
export function useClampOverflow<T extends HTMLElement>(
  content: unknown,
  expanded: boolean,
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Same reason as useSameLine: the mount-time measurement can happen before
    // the card has its final width, and no window resize follows to correct it.
    // Safe to observe this element — the verdict only renders a sibling toggle,
    // so it can't resize what is being observed.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [content, expanded]);
  return [ref, overflows];
}

/**
 * True when `ref`'s content is too wide for its box, so a caller can swap in a
 * shortened form ("220k €" for "220 000 €"). Full text is always what gets
 * measured: after shrinking, the element fits by definition, so re-measuring it
 * would report "fits" and flip straight back. `shrunkAtWidth` records how wide
 * the box was when we gave up, and full text is only retried once the box grows
 * past that.
 *
 * Costs one ResizeObserver per card, so it opts out entirely when there is
 * nothing to shorten — most listing rows carry no price. No window resize
 * listener either: a window resize that changes this element's box already
 * fires the observer.
 */
export function useShortenOnOverflow<T extends HTMLElement>(
  content: unknown,
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [short, setShort] = useState(false);
  const shrunkAtWidth = useRef<number | null>(null);

  useLayoutEffect(() => {
    shrunkAtWidth.current = null;
    setShort((prev) => (prev ? false : prev));

    const el = ref.current;
    if (!el || !content) return undefined;

    const measure = () => {
      const node = ref.current;
      if (!node) return;
      const box = node.clientWidth;
      if (shrunkAtWidth.current !== null) {
        if (box > shrunkAtWidth.current) {
          shrunkAtWidth.current = null;
          setShort(false);
        }
        return;
      }
      if (node.scrollWidth > box + 1) {
        shrunkAtWidth.current = box;
        setShort(true);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => observer.disconnect();
  }, [content]);

  return [ref, short];
}

/**
 * Detects when a flex-constrained label has wrapped, then keeps its visible box
 * at min-content width so centered lines do not leave a large apparent gap
 * after a leading icon. The footer width at which wrapping began is retained so
 * the natural one-line label is retried only after more room becomes available.
 */
export function useCompactOnWrap<T extends HTMLElement>(
  content: unknown,
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [compact, setCompact] = useState(false);
  const compactRef = useRef(false);
  const wrappedAtWidth = useRef<number | null>(null);

  useLayoutEffect(() => {
    const label = ref.current;
    const button = label?.parentElement;
    const footer = button?.parentElement;
    if (!label || !button || !footer) return undefined;

    let frame = 0;
    let active = true;

    const setMode = (next: boolean) => {
      compactRef.current = next;
      setCompact((previous) => (previous === next ? previous : next));
    };

    const scheduleMeasure = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    const measure = () => {
      frame = 0;
      if (!active) return;

      const footerWidth = footer.clientWidth;
      if (compactRef.current) {
        if (
          wrappedAtWidth.current !== null
          && footerWidth > wrappedAtWidth.current + 1
        ) {
          wrappedAtWidth.current = null;
          setMode(false);
          scheduleMeasure();
        }
        return;
      }

      const lineHeight = Number.parseFloat(getComputedStyle(label).lineHeight);
      if (
        Number.isFinite(lineHeight)
        && label.clientHeight > lineHeight * 1.5
      ) {
        wrappedAtWidth.current = footerWidth;
        setMode(true);
      }
    };

    wrappedAtWidth.current = null;
    setMode(false);
    scheduleMeasure();

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(footer);
    observer.observe(label);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!active) return;
        wrappedAtWidth.current = null;
        setMode(false);
        scheduleMeasure();
      });
    }

    return () => {
      active = false;
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [content]);

  return [ref, compact];
}
